import generateUUID from './generateUUID';
/**
 * Creates an enhanced error object with detailed information
 *
 * @param {string} code - Error code from ERROR_CODES
 * @param {string} message - Human-readable error message
 * @param {Partial<ErrorDetails>} details - Additional error details
 * @param {Partial<ErrorResolution>} [resolution] - Resolution hints
 * @param {Partial<RecoveryInfo>} [recovery] - Recovery information
 * @returns {EnhancedError} An enhanced error object
 */
export const createError = (code, message, details, resolution, recovery) => {
    var _a;
    // Generate IDs if not provided
    const requestId = details.requestId || generateUUID();
    const correlationId = details.correlationId || generateUUID();
    // Create a JavaScript Error object
    const error = new Error(message);
    // Add enhanced properties to the error
    error.code = code;
    error.details = Object.assign({ component: details.component || 'unknown', origin: details.origin || 'client', requestId, timestamp: details.timestamp || Date.now(), correlationId }, details);
    if (resolution) {
        error.resolution = {
            possibleCauses: resolution.possibleCauses || [],
            suggestedFixes: resolution.suggestedFixes || [],
            documentationLinks: resolution.documentationLinks || [],
            codeExample: resolution.codeExample
        };
    }
    if (recovery) {
        error.recovery = {
            canAutoRecover: (_a = recovery.canAutoRecover) !== null && _a !== void 0 ? _a : false,
            recoveryStrategy: recovery.recoveryStrategy || 'none',
            estimatedRecoveryTime: recovery.estimatedRecoveryTime,
            recoveryAttempt: recovery.recoveryAttempt,
            maxRecoveryAttempts: recovery.maxRecoveryAttempts,
            recoveryHint: recovery.recoveryHint
        };
    }
    return error;
};
/**
 * Creates an error specific to connection issues with recovery information
 *
 * @param {string} message - Human-readable error message
 * @param {Partial<ErrorDetails>} details - Additional error details
 * @param {Partial<ErrorResolution>} [resolution] - Resolution hints
 * @param {Partial<RecoveryInfo>} [recovery] - Recovery information
 * @returns {EnhancedError} An enhanced connection error
 */
export const createConnectionError = (message, details, resolution, recovery) => {
    // Merge with default connection error details
    const connectionResolution = Object.assign({ possibleCauses: [
            'Network connection lost',
            'Server unreachable',
            'Connection timeout',
            'WebSocket connection failed'
        ], suggestedFixes: [
            'Check your internet connection',
            'Verify the server is running',
            'Try reconnecting after a brief delay'
        ] }, (resolution || {}));
    const connectionRecovery = Object.assign({ canAutoRecover: true, recoveryStrategy: 'exponential-backoff' }, (recovery || {}));
    return createError('CONNECTION_ERROR', message, details, connectionResolution, connectionRecovery);
};
/**
 * Creates an error specific to rate limiting issues with recovery information
 *
 * @param {string} message - Human-readable error message
 * @param {Partial<ErrorDetails>} details - Additional error details
 * @param {Partial<ErrorResolution>} [resolution] - Resolution hints
 * @param {Partial<RecoveryInfo>} [recovery] - Recovery information
 * @returns {EnhancedError} An enhanced rate limit error
 */
export const createRateLimitError = (message, details, resolution, recovery) => {
    // Merge with default rate limit error details
    const rateLimitResolution = Object.assign({ possibleCauses: [
            'Too many requests in a short period',
            'API rate limit exceeded',
            'Concurrent connection limit reached'
        ], suggestedFixes: [
            'Reduce request frequency',
            'Implement client-side rate limiting',
            'Wait for the rate limit reset period'
        ] }, (resolution || {}));
    const rateLimitRecovery = Object.assign({ canAutoRecover: true, recoveryStrategy: 'timed-retry' }, (recovery || {}));
    return createError('RATE_LIMIT', message, details, rateLimitResolution, rateLimitRecovery);
};
/* v8 ignore end */ 
//# sourceMappingURL=errorUtils.js.map